import JSZip from 'jszip';
import type { CustomerFeedbackFormData } from '../types/applicationForm';
import { formatYmdAsCrmDate } from '../utils/dateTime';
import { formatCffSubmittedByLabel } from './accountCreationUi';

const TEMPLATE_URL = '/assets/cff/cff-template.xlsx';
const SG_TZ = 'Asia/Singapore';

/** Sheet tab = last 3 digits of running no (CFF-2026-08-001 → 001). */
export function cffSheetTitle(applicationNo: string): string {
  const trimmed = applicationNo.trim();
  const tail = /-(\d{3})$/.exec(trimmed) ?? /(\d{3})\s*$/.exec(trimmed);
  if (tail) return tail[1];
  const digits = trimmed.replace(/\D/g, '');
  return (digits.slice(-3) || '001').padStart(3, '0');
}

/** Parse CFF-YYYY-MM-NNN → Date in that month (mid-month, SG). */
export function cffMonthDateFromApplicationNo(applicationNo: string, fallback = new Date()): Date {
  const m = /^CFF-(\d{4})-(\d{2})-/i.exec(applicationNo.trim());
  if (!m) return fallback;
  return new Date(`${m[1]}-${m[2]}-15T12:00:00+08:00`);
}

/**
 * Monthly SharePoint folder under the CFF root, e.g. `08. Aug'26`.
 */
export function monthlyCffFolderName(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SG_TZ,
    month: '2-digit',
    year: '2-digit',
  }).formatToParts(date);
  const mm = parts.find((p) => p.type === 'month')?.value ?? '01';
  const yy = parts.find((p) => p.type === 'year')?.value ?? '00';
  const mon = new Intl.DateTimeFormat('en-US', {
    timeZone: SG_TZ,
    month: 'short',
  }).format(date);
  return `${mm}. ${mon}'${yy}`;
}

/**
 * Legacy multi-tab workbook name (local download only):
 * `08. BTH Portal - CFF Form - Aug'26.xlsx`
 */
export function monthlyCffWorkbookFileName(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SG_TZ,
    month: '2-digit',
    year: '2-digit',
  }).formatToParts(date);
  const mm = parts.find((p) => p.type === 'month')?.value ?? '01';
  const yy = parts.find((p) => p.type === 'year')?.value ?? '00';
  const mon = new Intl.DateTimeFormat('en-US', {
    timeZone: SG_TZ,
    month: 'short',
  }).format(date);
  return `${mm}. BTH Portal - CFF Form - ${mon}'${yy}.xlsx`;
}

/** Prefer plain text dates so Excel/PDF never show ##### from narrow columns. */
function formatDateDisplay(iso: string): string {
  const t = iso.trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return formatYmdAsCrmDate(t);
  return t;
}

function escapeXmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function makeSiBlock(text: string): string {
  const needsPreserve = /^\s|\s$|\n|\r|\t/.test(text);
  const body = escapeXmlText(text);
  if (needsPreserve) return `<si><t xml:space="preserve">${body}</t></si>`;
  return `<si><t>${body}</t></si>`;
}

function extractSiBlocks(sharedStringsXml: string): string[] {
  const blocks: string[] = [];
  const re = /<si\b[^>]*>[\s\S]*?<\/si>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(sharedStringsXml))) blocks.push(m[0]);
  return blocks;
}

function buildSharedStringsXml(siBlocks: string[]): string {
  const count = siBlocks.length;
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    `<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${count}" uniqueCount="${count}">` +
    siBlocks.join('') +
    '</sst>'
  );
}

function cellMatchRe(address: string): RegExp {
  return new RegExp(`<c r="${address}"[^/]*/>|<c r="${address}"[^>]*>[\\s\\S]*?</c>`);
}

function extractStyleAttr(cellXml: string): string {
  const m = /\bs="(\d+)"/.exec(cellXml);
  return m ? ` s="${m[1]}"` : '';
}

/** Fresh GUID for OOXML xr:/drawing ids (duplicates trigger Excel Online "Workbook Repaired"). */
function newOoxmlGuid(): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
  return `{${id.toUpperCase()}}`;
}

/**
 * Excel Online repairs (and wrecks column widths) when multiple sheets/drawings
 * share the same xr:uid / creationId from a cloned template.
 * Only touch those attributes — broad id="{uuid}" rewrites can break drawings.
 */
function refreshOoxmlInstanceIds(xml: string): string {
  return xml
    .replace(/\bxr:uid="\{[0-9A-Fa-f-]{36}\}"/gi, () => `xr:uid="${newOoxmlGuid()}"`)
    .replace(/\bxr2:uid="\{[0-9A-Fa-f-]{36}\}"/gi, () => `xr2:uid="${newOoxmlGuid()}"`)
    .replace(/\bxr3:uid="\{[0-9A-Fa-f-]{36}\}"/gi, () => `xr3:uid="${newOoxmlGuid()}"`)
    .replace(
      /(<a16:creationId\b[^>]*\bid=")\{[0-9A-Fa-f-]{36}\}"/gi,
      (_full, prefix: string) => `${prefix}${newOoxmlGuid()}"`,
    );
}

function replaceCell(sheetXml: string, address: string, replacement: string): string {
  const re = cellMatchRe(address);
  if (!re.test(sheetXml)) {
    throw new Error(`Cell ${address} missing in CFF template.`);
  }
  return sheetXml.replace(re, replacement);
}

/**
 * Split reason into up to 5 Excel Reason rows (S19–S23).
 * Enter = hard break; long continuous text soft-breaks into the next cell by width.
 * Does not use wrap-text inside one cell.
 */
export function splitReasonForExcelRows(reason: string, maxRows = 5, maxChars = 28): string[] {
  const rawLines = reason.replace(/\r\n/g, '\n').split('\n');
  const lines: string[] = [];
  for (const raw of rawLines) {
    let rest = raw.trim();
    if (!rest) continue;
    while (rest && lines.length < maxRows) {
      if (rest.length <= maxChars) {
        lines.push(rest);
        break;
      }
      let breakAt = rest.lastIndexOf(' ', maxChars);
      if (breakAt < Math.floor(maxChars / 2)) {
        // Prefer hyphen for codes like INV-1234-12345
        const hyphenAt = rest.lastIndexOf('-', maxChars);
        breakAt = hyphenAt >= Math.floor(maxChars / 3) ? hyphenAt + 1 : maxChars;
      }
      lines.push(rest.slice(0, breakAt).trim());
      rest = rest.slice(breakAt).trim();
    }
    if (lines.length >= maxRows) break;
  }
  while (lines.length < maxRows) lines.push('');
  return lines.slice(0, maxRows);
}

/** Cell address → string value (empty string clears the cell). */
function buildCellUpdates(
  form: CustomerFeedbackFormData,
  applicationNo: string,
  options: { omitSubmitterNameForStamp?: boolean } = {},
): Record<string, string> {
  const updates: Record<string, string> = {
    S8: applicationNo || '',
    S6: form.date ? formatDateDisplay(form.date) : '',
    B8: form.request_type === 'return' ? '√' : '',
    H8: form.request_type === 'exchange' ? '√' : '',
    M8: form.request_type === 'replenish' ? '√' : '',
    H12: form.company_code || '',
    R12: form.company_name || '',
    H13: form.address || '',
    H14: form.postal_code || '',
    R14: form.territory_code || '',
    H15: form.contact_person || '',
    R15: form.contact_number || '',
    // Invoice block (template anchors):
    // B26 Invoice No. | F26 value | B28 Invoice Date | F28 value
    // B30 Credit Notes | F30 / J30 checkbox cells | G30 / K30 labels | P26 name
    F26: form.invoice_no || '',
    F28: form.invoice_date ? formatDateDisplay(form.invoice_date) : '',
    F30: form.credit_notes === 'required' ? '√' : '',
    J30: form.credit_notes === 'not_required' ? '√' : '',
    P26: formatCffSubmittedByLabel(form.submitted_by, form.invoice_issued_by, {
      omitSubmitterName: options.omitSubmitterNameForStamp,
    }),
    E34: form.warehouse_action === 'replenish' ? '☑ Replenish (补货)' : '☐ Replenish (补货)',
    L34: form.warehouse_action === 'exchange' ? '☑ Exchange (换货)' : '☐ Exchange (换货)',
    R34:
      form.warehouse_action === 'others'
        ? `☑ Others (其他): ${form.warehouse_action_others || ''}`.trim()
        : '☐ Others (其他):',
    E35: form.warehouse_reason || '',
    B36: `${form.verified_by || '_________________________________'}\nVerify By (审核)`,
    P36: `${form.approved_by || '_________________________________'}\nApproved by (批准)`,
    B42: `${form.ack_date ? formatDateDisplay(form.ack_date) : '_________________________________'}\nDate (日期)`,
  };

  const reasonRows = splitReasonForExcelRows(form.reason || '');
  for (let i = 0; i < 5; i += 1) {
    const row = 19 + i;
    const item = form.items[i] ?? { item_code: '', quantity: '', uom: '' };
    updates[`F${row}`] = item.item_code || '';
    updates[`O${row}`] = item.quantity || '';
    updates[`Q${row}`] = item.uom || '';
    updates[`S${row}`] = reasonRows[i] || '';
  }

  return updates;
}

let cachedTemplateBytes: ArrayBuffer | null = null;

async function loadTemplateBytes(): Promise<ArrayBuffer> {
  if (cachedTemplateBytes) return cachedTemplateBytes.slice(0);
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) throw new Error('Failed to load CFF Excel template.');
  cachedTemplateBytes = await res.arrayBuffer();
  return cachedTemplateBytes.slice(0);
}

function blobToBase64(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  });
}

function remapSharedStringIndices(sheetXml: string, offset: number): string {
  if (offset === 0) return sheetXml;
  return sheetXml.replace(/<c\b([^>]*\bt="s"[^>]*)>([\s\S]*?)<\/c>/g, (_full, attrs, inner) => {
    const nextInner = inner.replace(/<v>(\d+)<\/v>/g, (_v: string, n: string) => {
      return `<v>${Number(n) + offset}</v>`;
    });
    return `<c${attrs}>${nextInner}</c>`;
  });
}

function nextNumericSuffix(paths: string[], prefix: string, ext: string): number {
  let max = 0;
  const re = new RegExp(`^${prefix}(\\d+)\\.${ext}$`);
  for (const p of paths) {
    const base = p.split('/').pop() ?? '';
    const m = re.exec(base);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}

function maxRId(relsXml: string): number {
  let max = 0;
  const re = /\bId="rId(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(relsXml))) max = Math.max(max, Number(m[1]));
  return max;
}

function maxSheetId(workbookXml: string): number {
  let max = 0;
  const re = /\bsheetId="(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(workbookXml))) max = Math.max(max, Number(m[1]));
  return max;
}

function stripCalcChainParts(zip: JSZip): void {
  if (zip.file('xl/calcChain.xml')) zip.remove('xl/calcChain.xml');
}

/**
 * Patch the official template ZIP in place (no ExcelJS rewrite).
 * Preserves drawings / printerSettings so Excel and Graph PDF stay valid.
 */
async function buildFilledSingleSheetBuf(
  form: CustomerFeedbackFormData,
  applicationNo: string,
  options: { forPdf?: boolean; omitSubmitterNameForStamp?: boolean } = {},
): Promise<ArrayBuffer> {
  const title = cffSheetTitle(applicationNo);
  const updates = buildCellUpdates(form, applicationNo, {
    omitSubmitterNameForStamp: options.omitSubmitterNameForStamp,
  });
  const zip = await JSZip.loadAsync(await loadTemplateBytes());

  const sstFile = zip.file('xl/sharedStrings.xml');
  if (!sstFile) throw new Error('CFF template missing sharedStrings.xml');
  const siBlocks = extractSiBlocks(await sstFile.async('string'));

  let sheetXml = await zip.file('xl/worksheets/sheet1.xml')!.async('string');
  for (const [address, value] of Object.entries(updates)) {
    const existing = sheetXml.match(cellMatchRe(address))?.[0] ?? '';
    const style = extractStyleAttr(existing);
    if (!value) {
      sheetXml = replaceCell(sheetXml, address, `<c r="${address}"${style}/>`);
      continue;
    }
    const idx = siBlocks.length;
    siBlocks.push(makeSiBlock(value));
    sheetXml = replaceCell(
      sheetXml,
      address,
      `<c r="${address}"${style} t="s"><v>${idx}</v></c>`,
    );
  }
  // Do not rewrite sheet/drawing instance IDs on single-sheet workbooks — Graph PDF
  // convert is picky. Unique IDs are applied only when appending into a multi-sheet file.
  zip.file('xl/worksheets/sheet1.xml', sheetXml);
  zip.file('xl/sharedStrings.xml', buildSharedStringsXml(siBlocks));

  let workbookXml = await zip.file('xl/workbook.xml')!.async('string');
  if (!options.forPdf) {
    // Excel tabs use 001/002/… ; PDF keeps template sheet name for Graph convert.
    workbookXml = workbookXml.replace(
      /name="Application Form"/,
      `name="${escapeXmlAttr(title)}"`,
    );
    workbookXml = workbookXml.replace(
      /'Application Form'!/,
      `'${title.replace(/'/g, "''")}'!`,
    );
  }
  // Clear machine-local absPath only — keep AlternateContent / revisionPtr / codeName.
  workbookXml = workbookXml.replace(
    /(<x15ac:absPath\b[^>]*\burl=")[^"]*(")/,
    '$1$2',
  );
  zip.file('xl/workbook.xml', workbookXml);

  // calcChain points at template cells and breaks after edits.
  stripCalcChainParts(zip);
  let wbRels = await zip.file('xl/_rels/workbook.xml.rels')!.async('string');
  if (/calcChain\.xml/.test(wbRels)) {
    wbRels = wbRels.replace(/<Relationship[^>]*Target="calcChain\.xml"[^>]*\/>/g, '');
    zip.file('xl/_rels/workbook.xml.rels', wbRels);
  }
  let ct = await zip.file('[Content_Types].xml')!.async('string');
  if (/calcChain\.xml/.test(ct)) {
    ct = ct.replace(/<Override[^>]*PartName="\/xl\/calcChain\.xml"[^>]*\/>/g, '');
    zip.file('[Content_Types].xml', ct);
  }

  // Keep customXml for Graph PDF — stripping it caused InvalidFileFormat / 406.

  return zip.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

async function buildFilledSingleSheetBlob(
  form: CustomerFeedbackFormData,
  applicationNo: string,
  options: { forPdf?: boolean; omitSubmitterNameForStamp?: boolean } = {},
): Promise<Blob> {
  const buf = await buildFilledSingleSheetBuf(form, applicationNo, options);
  return new Blob([new Uint8Array(buf)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

function listWorksheetPaths(zip: JSZip): string[] {
  return Object.keys(zip.files).filter(
    (p) => p.startsWith('xl/worksheets/sheet') && p.endsWith('.xml') && !p.includes('_rels'),
  );
}

function pieceWorksheetPath(piece: JSZip): string {
  const paths = listWorksheetPaths(piece);
  if (!paths.length) throw new Error('Filled CFF sheet XML missing.');
  return paths.includes('xl/worksheets/sheet1.xml')
    ? 'xl/worksheets/sheet1.xml'
    : paths[0];
}

function insertSheetTag(workbookXml: string, sheetTag: string, insertAt: number | null): string {
  if (/<sheets\s*\/>/.test(workbookXml)) {
    return workbookXml.replace(/<sheets\s*\/>/, `<sheets>${sheetTag}</sheets>`);
  }
  const sheetsMatch = /<sheets\b[^>]*>([\s\S]*?)<\/sheets>/.exec(workbookXml);
  if (!sheetsMatch) {
    return workbookXml.replace('</workbook>', `<sheets>${sheetTag}</sheets></workbook>`);
  }
  const inner = sheetsMatch[1];
  const tags = [...inner.matchAll(/<sheet\b[^>]*\/>/g)].map((m) => m[0]);
  const idx =
    insertAt == null || insertAt < 0 || insertAt > tags.length ? tags.length : insertAt;
  tags.splice(idx, 0, sheetTag);
  return (
    workbookXml.slice(0, sheetsMatch.index!) +
    `<sheets>${tags.join('')}</sheets>` +
    workbookXml.slice(sheetsMatch.index! + sheetsMatch[0].length)
  );
}

/** Rebuild Print_Area definedNames so localSheetId matches tab order. */
function rebuildPrintAreas(workbookXml: string): string {
  const names = [...workbookXml.matchAll(/<sheet\b[^>]*\bname="([^"]+)"/g)].map((m) => m[1]);
  let xml = workbookXml.replace(
    /<definedName[^>]*name="_xlnm\.Print_Area"[^>]*>[\s\S]*?<\/definedName>/g,
    '',
  );
  if (!names.length) return xml;
  const printNames = names
    .map(
      (name, i) =>
        `<definedName name="_xlnm.Print_Area" localSheetId="${i}">'${name.replace(/'/g, "''")}'!$A$1:$V$45</definedName>`,
    )
    .join('');
  if (xml.includes('</definedNames>')) {
    return xml.replace('</definedNames>', `${printNames}</definedNames>`);
  }
  if (xml.includes('<definedNames/>')) {
    return xml.replace('<definedNames/>', `<definedNames>${printNames}</definedNames>`);
  }
  return xml.replace('</workbook>', `<definedNames>${printNames}</definedNames></workbook>`);
}

/**
 * Append a one-sheet workbook into a master workbook via OOXML (JSZip).
 */
async function appendSheetWorkbook(
  masterBuf: ArrayBuffer,
  pieceBuf: ArrayBuffer,
  sheetName: string,
  insertAt: number | null = null,
): Promise<ArrayBuffer> {
  const master = await JSZip.loadAsync(masterBuf);
  const piece = await JSZip.loadAsync(pieceBuf);

  const pieceSheetPath = pieceWorksheetPath(piece);
  const pieceSheet = piece.file(pieceSheetPath);
  if (!pieceSheet) throw new Error('Filled CFF sheet XML missing.');

  const worksheetPaths = listWorksheetPaths(master);
  const sheetNum = nextNumericSuffix(worksheetPaths, 'sheet', 'xml');
  const drawingPaths = Object.keys(master.files).filter(
    (p) => p.startsWith('xl/drawings/drawing') && p.endsWith('.xml') && !p.includes('_rels'),
  );
  const drawingNum = nextNumericSuffix(drawingPaths, 'drawing', 'xml');
  const printerPaths = Object.keys(master.files).filter((p) =>
    p.startsWith('xl/printerSettings/printerSettings'),
  );
  const printerNum = nextNumericSuffix(printerPaths, 'printerSettings', 'bin');

  const masterSstFile = master.file('xl/sharedStrings.xml');
  const pieceSstFile = piece.file('xl/sharedStrings.xml');
  const masterSi = masterSstFile ? extractSiBlocks(await masterSstFile.async('string')) : [];
  const pieceSi = pieceSstFile ? extractSiBlocks(await pieceSstFile.async('string')) : [];
  const sstOffset = masterSi.length;
  master.file('xl/sharedStrings.xml', buildSharedStringsXml([...masterSi, ...pieceSi]));

  let sheetXml = await pieceSheet.async('string');
  sheetXml = remapSharedStringIndices(sheetXml, sstOffset);
  sheetXml = refreshOoxmlInstanceIds(sheetXml);
  master.file(`xl/worksheets/sheet${sheetNum}.xml`, sheetXml);

  const pieceBase = pieceSheetPath.split('/').pop()!;
  const pieceSheetRels = piece.file(`xl/worksheets/_rels/${pieceBase}.rels`);
  if (pieceSheetRels) {
    let rels = await pieceSheetRels.async('string');
    const hasDrawing = /drawings\/drawing\d+\.xml/.test(rels);
    const hasPrinter = /printerSettings\/printerSettings\d+\.bin/.test(rels);

    if (hasDrawing) {
      const pieceDrawing =
        piece.file('xl/drawings/drawing1.xml') ||
        piece.file(
          Object.keys(piece.files).find(
            (p) => p.startsWith('xl/drawings/drawing') && p.endsWith('.xml') && !p.includes('_rels'),
          ) || '',
        );
      if (pieceDrawing) {
        const drawingXml = refreshOoxmlInstanceIds(await pieceDrawing.async('string'));
        master.file(`xl/drawings/drawing${drawingNum}.xml`, drawingXml);
        master.file(
          `xl/drawings/_rels/drawing${drawingNum}.xml.rels`,
          '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
            '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image1.png"/>' +
            '</Relationships>',
        );
      }
      rels = rels.replace(
        /Target="[^"]*drawings\/drawing\d+\.xml"/,
        `Target="../drawings/drawing${drawingNum}.xml"`,
      );
    }

    if (hasPrinter) {
      const piecePrinter =
        piece.file('xl/printerSettings/printerSettings1.bin') ||
        piece.file(
          Object.keys(piece.files).find((p) =>
            p.startsWith('xl/printerSettings/printerSettings'),
          ) || '',
        );
      if (piecePrinter) {
        master.file(
          `xl/printerSettings/printerSettings${printerNum}.bin`,
          await piecePrinter.async('uint8array'),
        );
      }
      rels = rels.replace(
        /Target="[^"]*printerSettings\/printerSettings\d+\.bin"/,
        `Target="../printerSettings/printerSettings${printerNum}.bin"`,
      );
    }

    master.file(`xl/worksheets/_rels/sheet${sheetNum}.xml.rels`, rels);
  }

  let contentTypes = await master.file('[Content_Types].xml')!.async('string');
  if (!contentTypes.includes(`/xl/worksheets/sheet${sheetNum}.xml`)) {
    contentTypes = contentTypes.replace(
      '</Types>',
      `<Override PartName="/xl/worksheets/sheet${sheetNum}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
        (piece.file('xl/drawings/drawing1.xml')
          ? `<Override PartName="/xl/drawings/drawing${drawingNum}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`
          : '') +
        '</Types>',
    );
    master.file('[Content_Types].xml', contentTypes);
  }

  let wbRels = await master.file('xl/_rels/workbook.xml.rels')!.async('string');
  const newRId = `rId${maxRId(wbRels) + 1}`;
  wbRels = wbRels.replace(
    '</Relationships>',
    `<Relationship Id="${newRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${sheetNum}.xml"/></Relationships>`,
  );
  master.file('xl/_rels/workbook.xml.rels', wbRels);

  let workbookXml = await master.file('xl/workbook.xml')!.async('string');
  const newSheetId = maxSheetId(workbookXml) + 1;
  const sheetTag = `<sheet name="${escapeXmlAttr(sheetName)}" sheetId="${newSheetId}" r:id="${newRId}"/>`;
  workbookXml = insertSheetTag(workbookXml, sheetTag, insertAt);
  workbookXml = rebuildPrintAreas(workbookXml);
  master.file('xl/workbook.xml', workbookXml);

  stripCalcChainParts(master);
  let rels2 = await master.file('xl/_rels/workbook.xml.rels')!.async('string');
  if (/calcChain\.xml/.test(rels2)) {
    rels2 = rels2.replace(/<Relationship[^>]*Target="calcChain\.xml"[^>]*\/>/g, '');
    master.file('xl/_rels/workbook.xml.rels', rels2);
  }
  let ct2 = await master.file('[Content_Types].xml')!.async('string');
  if (/calcChain\.xml/.test(ct2)) {
    ct2 = ct2.replace(/<Override[^>]*PartName="\/xl\/calcChain\.xml"[^>]*\/>/g, '');
    master.file('[Content_Types].xml', ct2);
  }

  return master.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/** Remove a worksheet by tab name from a workbook ZIP (OOXML). */
async function removeSheetByName(masterBuf: ArrayBuffer, sheetName: string): Promise<ArrayBuffer> {
  const master = await JSZip.loadAsync(masterBuf);
  let workbookXml = await master.file('xl/workbook.xml')!.async('string');
  let wbRels = await master.file('xl/_rels/workbook.xml.rels')!.async('string');

  const sheetRe = new RegExp(
    `<sheet[^>]*name="${escapeXmlAttr(sheetName)}"[^>]*r:id="(rId\\d+)"[^>]*/>`,
  );
  const sheetReAlt = new RegExp(
    `<sheet[^>]*r:id="(rId\\d+)"[^>]*name="${escapeXmlAttr(sheetName)}"[^>]*/>`,
  );
  const m = sheetRe.exec(workbookXml) || sheetReAlt.exec(workbookXml);
  if (!m) return masterBuf;

  const rId = m[1];
  const relRe = new RegExp(`<Relationship[^>]*Id="${rId}"[^>]*Target="([^"]+)"[^>]*/>`);
  const relM = relRe.exec(wbRels);
  if (!relM) return masterBuf;

  const target = relM[1].replace(/^\.\//, '');
  const sheetPath = target.startsWith('xl/') ? target : `xl/${target}`;
  const sheetBase = sheetPath.split('/').pop()!;
  const sheetRelsPath = `xl/worksheets/_rels/${sheetBase}.rels`;

  const sheetRelsFile = master.file(sheetRelsPath);
  if (sheetRelsFile) {
    const rels = await sheetRelsFile.async('string');
    const drawM = /Target="\.\.\/drawings\/(drawing\d+\.xml)"/.exec(rels);
    if (drawM) {
      const drawingPath = `xl/drawings/${drawM[1]}`;
      const drawingRels = `xl/drawings/_rels/${drawM[1]}.rels`;
      if (master.file(drawingPath)) master.remove(drawingPath);
      if (master.file(drawingRels)) master.remove(drawingRels);
      let ct = await master.file('[Content_Types].xml')!.async('string');
      ct = ct.replace(
        new RegExp(`<Override[^>]*PartName="/xl/drawings/${drawM[1]}"[^>]*/>`, 'g'),
        '',
      );
      master.file('[Content_Types].xml', ct);
    }
  }

  if (master.file(sheetPath)) master.remove(sheetPath);
  if (master.file(sheetRelsPath)) master.remove(sheetRelsPath);

  workbookXml = workbookXml.replace(m[0], '');
  workbookXml = rebuildPrintAreas(workbookXml);
  master.file('xl/workbook.xml', workbookXml);

  wbRels = wbRels.replace(relM[0], '');
  master.file('xl/_rels/workbook.xml.rels', wbRels);

  let ct2 = await master.file('[Content_Types].xml')!.async('string');
  ct2 = ct2.replace(new RegExp(`<Override[^>]*PartName="/${sheetPath}"[^>]*/>`, 'g'), '');
  master.file('[Content_Types].xml', ct2);

  stripCalcChainParts(master);
  return master.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

async function listSheetNames(buf: ArrayBuffer): Promise<string[]> {
  const zip = await JSZip.loadAsync(buf);
  const workbookXml = await zip.file('xl/workbook.xml')!.async('string');
  return [...workbookXml.matchAll(/<sheet\b[^>]*\bname="([^"]+)"/g)].map((m) => m[1]);
}

/** Delete tab completely, then rewrite from filled template at the same position. */
async function replaceSheetByDeleteAndRewrite(
  masterBuf: ArrayBuffer,
  pieceBuf: ArrayBuffer,
  sheetName: string,
): Promise<ArrayBuffer> {
  const names = await listSheetNames(masterBuf);
  const insertAt = names.indexOf(sheetName);
  const without = await removeSheetByName(masterBuf, sheetName);
  return appendSheetWorkbook(without, pieceBuf, sheetName, insertAt >= 0 ? insertAt : null);
}

function isLegacyOrCorruptMaster(sheetNames: string[]): boolean {
  const legacy = new Set([
    'Summary',
    'Master',
    'Application Form',
    'Customer Information',
  ]);
  return sheetNames.some((n) => legacy.has(n));
}

/** PDF-stripped masters (no customXml) often trigger Excel repair — treat as unhealthy. */
async function masterLooksHealthy(buf: ArrayBuffer): Promise<boolean> {
  try {
    const zip = await JSZip.loadAsync(buf);
    if (!zip.file('xl/workbook.xml') || !zip.file('xl/styles.xml')) return false;
    // Prefer masters that still look like the official template package.
    const hasCustomXml = Object.keys(zip.files).some((p) => p.startsWith('customXml/'));
    const hasPrinter = Object.keys(zip.files).some((p) =>
      p.startsWith('xl/printerSettings/'),
    );
    return hasCustomXml && hasPrinter;
  } catch {
    return false;
  }
}

/**
 * Monthly shared workbook: one sheet per running no (001, 002, …).
 */
export async function buildCffMasterWorkbookBlob(
  existingMaster: ArrayBuffer | null,
  form: CustomerFeedbackFormData,
  applicationNo: string,
): Promise<Blob> {
  const title = cffSheetTitle(applicationNo);
  const filledBuf = await buildFilledSingleSheetBuf(form, applicationNo, { forPdf: false });
  const filledBlob = new Blob([new Uint8Array(filledBuf)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  if (!existingMaster) return filledBlob;

  try {
    const names = await listSheetNames(existingMaster);
    // Old / repaired / PDF-stripped masters — start clean so Excel won't ask to repair.
    if (isLegacyOrCorruptMaster(names) || !(await masterLooksHealthy(existingMaster))) {
      return filledBlob;
    }

    if (names.includes(title)) {
      const replaced = await replaceSheetByDeleteAndRewrite(existingMaster, filledBuf, title);
      return new Blob([new Uint8Array(replaced)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    }

    const merged = await appendSheetWorkbook(existingMaster, filledBuf, title);
    return new Blob([new Uint8Array(merged)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  } catch {
    return filledBlob;
  }
}

/** Single-sheet workbook used only for Graph PDF convert of the current CFF. */
export async function buildCffPdfSourceBlob(
  form: CustomerFeedbackFormData,
  applicationNo: string,
  options: { omitSubmitterNameForStamp?: boolean } = {},
): Promise<Blob> {
  return buildFilledSingleSheetBlob(form, applicationNo, {
    forPdf: true,
    omitSubmitterNameForStamp: options.omitSubmitterNameForStamp,
  });
}

/** Excel-openable filled sheet (keeps template customXml — no repair dialog). */
export async function buildCffExcelSheetBlob(
  form: CustomerFeedbackFormData,
  applicationNo: string,
): Promise<Blob> {
  return buildFilledSingleSheetBlob(form, applicationNo, { forPdf: false });
}

export async function buildCffMasterWorkbookBase64(
  existingMaster: ArrayBuffer | null,
  form: CustomerFeedbackFormData,
  applicationNo: string,
): Promise<string> {
  return blobToBase64(await buildCffMasterWorkbookBlob(existingMaster, form, applicationNo));
}

export async function buildCffPdfSourceBase64(
  form: CustomerFeedbackFormData,
  applicationNo: string,
  options: { omitSubmitterNameForStamp?: boolean } = {},
): Promise<string> {
  return blobToBase64(await buildCffPdfSourceBlob(form, applicationNo, options));
}

/** Base64 sheet for SharePoint monthly merge (Excel-safe, not PDF-stripped). */
export async function buildCffSyncSheetBase64(
  form: CustomerFeedbackFormData,
  applicationNo: string,
): Promise<string> {
  return blobToBase64(await buildCffExcelSheetBlob(form, applicationNo));
}

export async function downloadCffExcel(
  form: CustomerFeedbackFormData,
  applicationNo: string,
  existingMaster: ArrayBuffer | null = null,
): Promise<void> {
  // Prefer a clean Excel-safe workbook. Skip merging stripped/corrupt SharePoint masters.
  const blob = await buildCffMasterWorkbookBlob(existingMaster, form, applicationNo);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Match PDF download naming: reference no. (e.g. CFF-2026-10-070.xlsx)
  const safeNo = applicationNo.trim().replace(/[\\/:*?"<>|]+/g, '_') || 'CFF';
  a.download = `${safeNo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `YYYY-MM` from CFF-YYYY-MM-NNN (or empty if not parseable). */
export function cffYearMonthFromApplicationNo(applicationNo: string): string | null {
  const m = /^CFF-(\d{4})-(\d{2})-/i.exec(applicationNo.trim());
  return m ? `${m[1]}-${m[2]}` : null;
}

export function currentCffYearMonth(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SG_TZ,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '2026';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  return `${y}-${m}`;
}

/**
 * Build a fresh monthly workbook from many CFF rows (one tab each: 001, 002, …).
 * Does not read SharePoint — safe rebuild after deleting the monthly file.
 */
export async function buildMonthlyCffWorkbookBlob(
  entries: Array<{ form: CustomerFeedbackFormData; applicationNo: string }>,
): Promise<Blob> {
  const sorted = [...entries].sort((a, b) =>
    a.applicationNo.localeCompare(b.applicationNo, undefined, { numeric: true }),
  );
  if (!sorted.length) throw new Error('No CFF forms to export for this month.');

  let master: ArrayBuffer | null = null;
  for (const entry of sorted) {
    const blob = await buildCffMasterWorkbookBlob(master, entry.form, entry.applicationNo);
    master = await blob.arrayBuffer();
  }
  return new Blob([new Uint8Array(master!)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export async function downloadMonthlyCffExcel(
  entries: Array<{ form: CustomerFeedbackFormData; applicationNo: string }>,
  yearMonth: string,
): Promise<void> {
  const blob = await buildMonthlyCffWorkbookBlob(entries);
  const date = /^\d{4}-\d{2}$/.test(yearMonth)
    ? new Date(`${yearMonth}-15T12:00:00+08:00`)
    : new Date();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = monthlyCffWorkbookFileName(date);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function monthlyCffWorkbookToBase64(
  entries: Array<{ form: CustomerFeedbackFormData; applicationNo: string }>,
): Promise<string> {
  return blobToBase64(await buildMonthlyCffWorkbookBlob(entries));
}

/** Flat master-list row for the CFF list page (one Excel table). */
export type CffMasterListRow = {
  applicationNo: string;
  status: string;
  companyName: string;
  companyCode: string;
  requestType: string;
  invoiceNo: string;
  itemCodes: string;
  reason: string;
  internalRemarks: string;
  submittedBy: string;
  createdAt: string;
};

function masterListFileName(yearMonth: string): string {
  const date = /^\d{4}-\d{2}$/.test(yearMonth)
    ? new Date(`${yearMonth}-15T12:00:00+08:00`)
    : new Date();
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SG_TZ,
    month: '2-digit',
    year: '2-digit',
  }).formatToParts(date);
  const mm = parts.find((p) => p.type === 'month')?.value ?? '01';
  const yy = parts.find((p) => p.type === 'year')?.value ?? '00';
  const mon = new Intl.DateTimeFormat('en-US', {
    timeZone: SG_TZ,
    month: 'short',
  }).format(date);
  return `${mm}. CFF Master List - ${mon}'${yy}.xlsx`;
}

/**
 * One-sheet master list for the selected month (list-page columns + creation date).
 */
export async function downloadCffMasterListExcel(
  rows: CffMasterListRow[],
  yearMonth: string,
): Promise<void> {
  if (!rows.length) throw new Error('No CFF forms to export for this month.');

  const ExcelJS = (await import('exceljs')).default;
  const { downloadWorkbook } = await import('./projectExport');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BTH Portal';
  const ws = wb.addWorksheet('Master List', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const headers = [
    'NCPAR No.',
    'Status',
    'Company',
    'Company Code',
    'Request',
    'Invoice No.',
    'Items No.',
    'Reason',
    'Internal Remarks',
    'Submitted By',
    'Creation Date',
  ];
  ws.addRow(headers);
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle', wrapText: true };

  for (const row of rows) {
    ws.addRow([
      row.applicationNo,
      row.status,
      row.companyName,
      row.companyCode,
      row.requestType,
      row.invoiceNo,
      row.itemCodes,
      row.reason,
      row.internalRemarks,
      row.submittedBy,
      row.createdAt,
    ]);
  }

  ws.columns = [
    { width: 20 },
    { width: 12 },
    { width: 36 },
    { width: 14 },
    { width: 12 },
    { width: 18 },
    { width: 28 },
    { width: 36 },
    { width: 36 },
    { width: 22 },
    { width: 20 },
  ];

  await downloadWorkbook(wb, masterListFileName(yearMonth));
}
